/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  $applyNodeReplacement,
  DOMConversion,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

export type SerializedVariableNode = Spread<
  {
    variableValue: string;
  },
  SerializedTextNode
>;

const variableStyle = '';

export class VariableNode extends TextNode {
  __variable: string;

  __value: string;

  static getType(): string {
    return 'variable';
  }

  static clone(node: VariableNode): VariableNode {
    return new VariableNode(node.__value, node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedVariableNode): VariableNode {
    return $createVariableNode(serializedNode.variableValue).updateFromJSON(serializedNode);
  }

  constructor(variableValue: string, text?: string, key?: NodeKey) {
    super(variableValue, key);
    this.__variable = text || variableValue;
    this.__value = variableValue;
  }

  exportJSON(): SerializedVariableNode {
    return {
      ...super.exportJSON(),
      variableValue: this.__value,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = variableStyle;
    dom.className = 'font-bold text-litlingo-primary';
    dom.spellcheck = false;
    dom.title = this.__value;

    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-variable', 'true');
    element.setAttribute('data-lexical-variable-value', this.__value);

    element.textContent = `{${this.__value}}`;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement): DOMConversion<HTMLElement> | null => {
        if (!domNode.hasAttribute('data-lexical-variable')) {
          return null;
        }
        return {
          conversion: $convertVariableElement,
          priority: 1,
        };
      },
    };
  }

  static isTextEntity(): true {
    return true;
  }

  static canInsertTextBefore(): boolean {
    return false;
  }

  static canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createVariableNode(variableValue: string, textContent?: string): VariableNode {
  const variableNode = new VariableNode(variableValue, textContent || variableValue);
  variableNode.setMode('token').toggleDirectionless();
  return $applyNodeReplacement(variableNode);
}

function $convertVariableElement(domNode: HTMLElement): DOMConversionOutput | null {
  const { textContent } = domNode;
  // eslint-disable-next-line react/destructuring-assignment
  const variableValue = domNode.getAttribute('data-lexical-variable-value');

  if (textContent !== null) {
    const node = $createVariableNode(
      typeof variableValue === 'string' ? variableValue : textContent,
      textContent
    );
    return {
      node,
    };
  }

  return null;
}

export function $isVariableNode(node: LexicalNode | null | undefined): node is VariableNode {
  return node instanceof VariableNode;
}
